/**
 * React context surface of the theme system: `useTheme()` and `ThemeProvider`.
 *
 * The provider merges its sparse `theme` prop over the parent context value
 * (nested providers compose), memoizes the result, and publishes it twice from
 * the same object so the two surfaces cannot drift: as React context for
 * `useTheme()` consumers, and as CSS custom properties via `ThemeScope` for
 * CSS Modules. Rendering without a provider falls back to `defaultTheme` — the
 * context default.
 */

import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';

import { ThemeScope } from './scope';
import { mergeTheme } from './theme.helpers';
import type { PartialTheme, Theme } from './tokens';
import { defaultTheme } from './tokens';

const ThemeContext = createContext<Theme>(defaultTheme);

/**
 * Returns the resolved design tokens from the nearest `ThemeProvider`, or
 * `defaultTheme` when rendered without one.
 * @public
 */
export function useTheme(): Theme {
    return useContext(ThemeContext);
}

/**
 * Publishes a theme to the subtree. `theme` is merged over the parent
 * provider's tokens (or `defaultTheme` at the outermost provider); the result
 * is readable via `useTheme()` and, on web, as CSS custom properties on an
 * invisible `display: contents` wrapper. The provider never reads `window` —
 * resolving which theme applies is the caller's concern.
 * @public
 */
export function ThemeProvider({ theme, children }: { theme?: PartialTheme; children: ReactNode }) {
    const parent = useContext(ThemeContext);
    const tokens = useMemo(() => mergeTheme(parent, theme), [parent, theme]);
    return (
        <ThemeContext.Provider value={tokens}>
            <ThemeScope tokens={tokens}>{children}</ThemeScope>
        </ThemeContext.Provider>
    );
}
